import { capitalize } from '@fastre/core/helperFunctions/string'
import { ownsListing } from '@fastre/core/schemas/listing'
import { UpdateConjunctionalAgreementSchema } from '@fastre/core/schemas/saleListing'
import { zodResolver } from '@hookform/resolvers/zod'
import { DeleteRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Sheet,
    Stack,
    Switch,
    Table,
    Typography,
    useTheme,
} from '@mui/joy'
import { useApi } from 'api'
import { useFindUserFromId } from 'apiProviders'
import { useUserData } from 'auth'
import AgentInput from 'components/agentInput'
import { SlotInput } from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { isBelowBreakpoint, useIsMobile } from 'helperFunctions/pageSize'
import { prop } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import {
    Controller,
    FormProvider,
    SubmitHandler,
    useFieldArray,
    useForm,
    useFormContext,
} from 'react-hook-form'
import { useEditMode } from './editMode'
import { useListingContext } from './listingProvider'

const MobileFriendlySwitch = <T1 extends string, T2 extends string>({
    value,
    onChange,
    options,
}: {
    value: T1 | T2
    onChange: (value: T1 | T2) => void
    options: [T1, T2]
}) => {
    const { editMode } = useEditMode()
    const isMobile = useIsMobile()
    const theme = useTheme()

    return (
        <FormControl sx={{ minWidth: '160px', width: 'unset!important', alignItems: 'flex-start' }}>
            {editMode ? (
                isBelowBreakpoint('lg') ? (
                    <RadioGroup
                        value={value}
                        onChange={e => onChange(e.target.value as T1 | T2)}
                        orientation="vertical"
                    >
                        {options.map(option => (
                            <Radio
                                key={option}
                                value={option}
                                label={capitalize(option)}
                                size="sm"
                            />
                        ))}
                    </RadioGroup>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <Switch
                            checked={value === options[1]}
                            onChange={e => onChange(e.target.checked ? options[1] : options[0])}
                            startDecorator={({ checked }) => (
                                <Typography
                                    level="body-sm"
                                    sx={{ opacity: !checked ? 1 : 0.35 }}
                                >
                                    {capitalize(options[0])}
                                </Typography>
                            )}
                            endDecorator={({ checked }) => (
                                <Typography
                                    level="body-sm"
                                    sx={{ opacity: checked ? 1 : 0.35 }}
                                >
                                    {capitalize(options[1])}
                                </Typography>
                            )}
                        />
                    </Box>
                )
            ) : (
                capitalize(value)
            )}
        </FormControl>
    )
}

const Line = ({
    index,
    removeAgreement,
    disabled,
}: {
    index: number
    removeAgreement: () => void
    disabled: boolean
}) => {
    const { watch, control, setValue } = useFormContext()
    const isMobile = useIsMobile()
    const { editMode } = useEditMode()

    const agreementType = watch(`conjunctionalAgreements.${index}.agreementType`)
    const type = watch(`conjunctionalAgreements.${index}.type`)

    return (
        <tr>
            <td>
                <Controller
                    control={control}
                    disabled={false}
                    name={`conjunctionalAgreements.${index}.type`}
                    render={field => (
                        <MobileFriendlySwitch
                            //{/*...dissoc('value', field.field)*/}
                            options={['internal', 'external']}
                            //checked={field.field.value == 'external'}
                            onChange={field.field.onChange}
                            value={field.field.value}
                        />
                    )}
                />
            </td>
            <td>
                {type == 'external' && (
                    <Controller
                        control={control}
                        disabled={disabled}
                        name={`conjunctionalAgreements.${index}.referrerName`}
                        render={field => <SlotInput {...field} />}
                    />
                )}
                {type == 'internal' && (
                    <Controller
                        control={control}
                        disabled={disabled}
                        name={`conjunctionalAgreements.${index}.agentId`}
                        render={field => <AgentInput {...field.field} />}
                    />
                )}
            </td>
            <td>
                <Controller
                    control={control}
                    disabled={disabled}
                    name={`conjunctionalAgreements.${index}.agreementType`}
                    render={field => (
                        <MobileFriendlySwitch
                            options={['fixed', 'percentage']}
                            value={field.field.value}
                            onChange={field.field.onChange}
                        />
                    )}
                />
            </td>
            <td>
                {agreementType === 'fixed' && (
                    <Controller
                        control={control}
                        disabled={disabled}
                        name={`conjunctionalAgreements.${index}.commissionAmount`}
                        render={field => (
                            <SlotInput
                                type="dollar"
                                {...field}
                            />
                        )}
                    />
                )}
                {agreementType === 'percentage' && (
                    <Controller
                        control={control}
                        disabled={disabled}
                        name={`conjunctionalAgreements.${index}.commissionPercent`}
                        render={field => (
                            <SlotInput
                                type="number"
                                endDecorator="%"
                                {...field}
                            />
                        )}
                    />
                )}
            </td>
            {!disabled && editMode && (
                <td style={{ width: '36px' }}>
                    <IconButton
                        onClick={removeAgreement}
                        sx={{ mt: 'auto', mb: 1 }}
                        color="danger"
                    >
                        <DeleteRounded />
                    </IconButton>
                </td>
            )}
        </tr>
    )
}

const MobileAgreementSheet = ({
    index,
    removeAgreement,
    disabled,
}: {
    index: number
    removeAgreement: () => void
    disabled: boolean
}) => {
    const { watch, control } = useFormContext()
    const { editMode } = useEditMode()

    const agreementType = watch(`conjunctionalAgreements.${index}.agreementType`)
    const type = watch(`conjunctionalAgreements.${index}.type`)

    return (
        <Sheet
            variant="outlined"
            sx={{
                p: 2,
                mb: 2,
                borderRadius: 'sm',
                position: 'relative',
            }}
        >
            {!disabled && editMode && (
                <IconButton
                    onClick={removeAgreement}
                    color="danger"
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        zIndex: 1000,
                    }}
                    size="sm"
                >
                    <DeleteRounded />
                </IconButton>
            )}
            <Stack spacing={2}>
                <FormControl>
                    <FormLabel>Type</FormLabel>
                    <Controller
                        control={control}
                        disabled={false}
                        name={`conjunctionalAgreements.${index}.type`}
                        render={field => (
                            <MobileFriendlySwitch
                                options={['internal', 'external']}
                                onChange={field.field.onChange}
                                value={field.field.value}
                            />
                        )}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Referrer</FormLabel>
                    {type === 'external' && (
                        <Controller
                            control={control}
                            disabled={disabled}
                            name={`conjunctionalAgreements.${index}.referrerName`}
                            render={field => <SlotInput {...field} />}
                        />
                    )}
                    {type === 'internal' && (
                        <Controller
                            control={control}
                            disabled={disabled}
                            name={`conjunctionalAgreements.${index}.agentId`}
                            render={field => <AgentInput {...field.field} />}
                        />
                    )}
                </FormControl>

                <FormControl>
                    <FormLabel>Agreement Type</FormLabel>
                    <Controller
                        control={control}
                        disabled={disabled}
                        name={`conjunctionalAgreements.${index}.agreementType`}
                        render={field => (
                            <MobileFriendlySwitch
                                options={['fixed', 'percentage']}
                                value={field.field.value}
                                onChange={field.field.onChange}
                            />
                        )}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Amount (inc. GST)</FormLabel>
                    {agreementType === 'fixed' && (
                        <Controller
                            control={control}
                            disabled={disabled}
                            name={`conjunctionalAgreements.${index}.commissionAmount`}
                            render={field => (
                                <SlotInput
                                    type="dollar"
                                    {...field}
                                />
                            )}
                        />
                    )}
                    {agreementType === 'percentage' && (
                        <Controller
                            control={control}
                            disabled={disabled}
                            name={`conjunctionalAgreements.${index}.commissionPercent`}
                            render={field => (
                                <SlotInput
                                    type="number"
                                    endDecorator="%"
                                    {...field}
                                />
                            )}
                        />
                    )}
                </FormControl>
            </Stack>
        </Sheet>
    )
}

const ConjunctionalAgreement = forwardRef(({}, ref: any) => {
    const { listing, setListing } = useListingContext()
    const api = useApi()
    const showSnack = useShowSnack()
    const { user } = useUserData()
    const { editMode } = useEditMode()
    const findUserFromId = useFindUserFromId()
    const isMobile = useIsMobile()

    const formDisabled =
        !user.permissions.includes('listings.fullControl') && !ownsListing(listing)(user.userId)

    const hookForm = useForm<UpdateConjunctionalAgreementSchema>({
        resolver: zodResolver(UpdateConjunctionalAgreementSchema),
        defaultValues: {
            conjunctionalAgreements: listing.conjunctionalAgreements ?? [
                {
                    type: 'external',
                    referrerName: '',
                    agreementType: 'fixed',
                    commissionAmount: 0,
                },
            ],
        },
    })

    const { control, handleSubmit, formState, getValues, setValue, trigger, reset, watch } = hookForm

    const {
        fields: conjunctionalAgreements,
        append: appendAgreement,
        remove: removeAgreement,
    } = useFieldArray({
        control,
        name: 'conjunctionalAgreements',
    })

    useEffect(() => {
        reset({
            conjunctionalAgreements: listing.conjunctionalAgreements,
        })
    }, [listing.listingId])

    const [submitLoading, setSubmitLoading] = useState(false)

    const submitForm: SubmitHandler<UpdateConjunctionalAgreementSchema> = async data => {
        setSubmitLoading(true)
        try {
            const updatedListingData = await api
                .post(`/listing/sale/${listing.listingId}/updateconjunctionalagreements`, data)
                .then(prop('data'))

            const updatedListing = { ...listing, ...updatedListingData }

            setListing(updatedListing)
            reset(updatedListing)
            showSnack('Conjunctional agreements updated', 'success')
            return true
        } catch (e) {
            console.error(e)
            showSnack('Error saving conjunctional agreements', 'danger')
        } finally {
            setSubmitLoading(false)
        }
    }

    useImperativeHandle(ref, () => ({
        saveForm: () => submitForm(getValues()),
        resetForm: () =>
            reset({
                conjunctionalAgreements: listing.conjunctionalAgreements,
            }),
    }))

    if (!editMode && (listing.conjunctionalAgreements?.length ?? 0) == 0) {
        return <Typography>No conjunctional agreements</Typography>
    }

    if (!editMode) {
        return (
            <Table sx={{ maxWidth: '500px' }}>
                <thead>
                    <tr>
                        <th>Refferer</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {conjunctionalAgreements.map(conj => (
                        <tr key={conj.id}>
                            <td>
                                {conj.type == 'external'
                                    ? conj.referrerName
                                    : findUserFromId(conj.agentId)
                                          .map(x => `${x.firstName} ${x.lastName}`)
                                          .orSome('')}
                            </td>
                            <td>
                                {conj.agreementType == 'fixed'
                                    ? `$${conj.commissionAmount}`
                                    : `${conj.commissionPercent}%`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        )
    }

    return (
        <Box>
            <FormProvider {...hookForm}>
                <form noValidate>
                    {isMobile ? (
                        <Stack spacing={2}>
                            {conjunctionalAgreements.map((conj, index) => (
                                <MobileAgreementSheet
                                    key={conj.id}
                                    index={index}
                                    removeAgreement={() => removeAgreement(index)}
                                    disabled={formDisabled}
                                />
                            ))}
                            {!formDisabled && editMode && (
                                <Button
                                    variant="soft"
                                    onClick={() =>
                                        appendAgreement({
                                            type: 'external',
                                            referrerName: '',
                                            agreementType: 'fixed',
                                            commissionAmount: 0,
                                        })
                                    }
                                >
                                    Add Agreement
                                </Button>
                            )}
                        </Stack>
                    ) : (
                        <>
                            <Table
                                sx={{
                                    '& td': {
                                        borderBottom: 'none !important',
                                    },
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td>
                                            <FormLabel>Type</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Referrer</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Agreement</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Amount (inc. GST)</FormLabel>
                                        </td>
                                        {!formDisabled && editMode && <td style={{ width: '40px' }} />}
                                    </tr>
                                    {conjunctionalAgreements.map((conj, index) => (
                                        <Line
                                            key={conj.id}
                                            index={index}
                                            removeAgreement={() => removeAgreement(index)}
                                            disabled={formDisabled}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                            {!formDisabled && editMode && (
                                <Button
                                    variant="soft"
                                    sx={{ mt: 2 }}
                                    onClick={() =>
                                        appendAgreement({
                                            type: 'external',
                                            referrerName: '',
                                            agreementType: 'fixed',
                                            commissionAmount: 0,
                                        })
                                    }
                                >
                                    Add Agreement
                                </Button>
                            )}
                        </>
                    )}
                </form>
            </FormProvider>
        </Box>
    )
})

export default ConjunctionalAgreement
